import type { Scorecard } from '@compass/client/types/scorecard';
import { Check, Database, Loader2, Pencil, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { FormattedDateRelative } from '@/components/FormattedDateRelative';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { inspectJson, JsonEditor } from '@/components/ui/json-editor';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { BridgeTraceSummary } from '@/services/langfuseBridge.service';
import type { SelectOption } from '@/types/formSchema';

import {
  useCreateDatasetItem,
  useDatasetItem,
  useDeleteDatasetItem,
  useLangfuseDatasets,
  useLangfuseTrace,
  useLangfuseTraces,
  useTraceDatasetMemberships,
  useUpdateDatasetItem,
} from '../hooks/useLangfuseDataset';
import { FindingsEditor } from './FindingsEditor';
import { TraceInspector } from './TraceInspector';

interface QcAddToDatasetDialogProps {
  callId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Scorecard for the evaluation — labels + grouping for the findings editor. */
  scorecard: Scorecard | null;
}

const prettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value ?? null, null, 2);
  } catch {
    return String(value ?? '');
  }
};

/**
 * One row in the trace rail. Fetches the trace's existing dataset memberships so
 * the user sees, at a glance, how many datasets a trace is already in.
 */
const TraceListItem: React.FC<{
  trace: BridgeTraceSummary;
  active: boolean;
  enabled: boolean;
  onSelect: () => void;
}> = ({ trace, active, enabled, onSelect }) => {
  const membershipsQuery = useTraceDatasetMemberships(trace.trace_id, enabled);
  const count = membershipsQuery.data?.length ?? 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'mb-1.5 flex w-full flex-col items-start gap-1.5 rounded-lg border px-3 py-2.5 text-left transition-colors',
        active
          ? 'border-primary bg-primary/5'
          : 'border-transparent hover:border-border hover:bg-muted/60',
      )}
    >
      <span className="w-full truncate text-sm font-medium text-foreground">
        {trace.name || trace.trace_id}
      </span>
      {trace.timestamp && (
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          Created <FormattedDateRelative value={trace.timestamp} />
        </span>
      )}
      {count > 0 && (
        <span className="flex items-center gap-1 text-xs font-medium text-positive-foreground">
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
          In {count} dataset{count > 1 ? 's' : ''}
        </span>
      )}
    </button>
  );
};

export const QcAddToDatasetDialog: React.FC<QcAddToDatasetDialogProps> = ({
  callId,
  open,
  onOpenChange,
  scorecard,
}) => {
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string>('');
  const [expectedOutput, setExpectedOutput] = useState<string>('');
  const [jsonValid, setJsonValid] = useState(true);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'trace' | 'dataset'>('trace');
  const seedKeyRef = useRef<string | null>(null);

  const tracesQuery = useLangfuseTraces(callId, open);
  const traceQuery = useLangfuseTrace(selectedTraceId);
  const membershipsQuery = useTraceDatasetMemberships(selectedTraceId, open);
  const datasetsQuery = useLangfuseDatasets(open);
  const createItem = useCreateDatasetItem();
  const updateItem = useUpdateDatasetItem();
  const deleteItem = useDeleteDatasetItem();

  const memberships = membershipsQuery.data ?? [];
  const existingMembership = useMemo(
    () => memberships.find(m => m.dataset_id === selectedDatasetId) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [membershipsQuery.data, selectedDatasetId],
  );
  const existingItemId = existingMembership?.item_id ?? null;
  const mode: 'add' | 'update' = existingItemId ? 'update' : 'add';

  // In update mode, load the existing item so the editor edits its real value.
  const itemQuery = useDatasetItem(selectedDatasetId || null, existingItemId, open);

  // Reset everything whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setSelectedTraceId(null);
      setSelectedDatasetId('');
      setExpectedOutput('');
      setJsonValid(true);
      setConfirmRemoveOpen(false);
      setActiveTab('trace');
      seedKeyRef.current = null;
    }
  }, [open]);

  // Seed the editor from whatever the primary action will write: the trace's
  // output (add) or the existing item's expected output (update). Reseeds only
  // when the target (trace / dataset / item) changes — never on keystrokes.
  useEffect(() => {
    if (!selectedTraceId) return;
    if (mode === 'update') {
      const item = itemQuery.data;
      if (item && item.id === existingItemId) {
        const key = `upd|${existingItemId}`;
        if (seedKeyRef.current !== key) {
          setExpectedOutput(prettyJson(item.expected_output));
          setJsonValid(true);
          seedKeyRef.current = key;
        }
      }
    } else {
      const trace = traceQuery.data;
      if (trace && trace.trace_id === selectedTraceId) {
        const key = `add|${selectedTraceId}`;
        if (seedKeyRef.current !== key) {
          setExpectedOutput(prettyJson(trace.output));
          setJsonValid(true);
          seedKeyRef.current = key;
        }
      }
    }
  }, [mode, itemQuery.data, traceQuery.data, selectedTraceId, existingItemId]);

  // When a member trace is selected, default the picker to (the first) dataset it
  // is already in → the modal opens directly in update mode. Only fills an empty
  // selection, so the user can switch to another dataset (to add) afterwards.
  useEffect(() => {
    if (!selectedTraceId || selectedDatasetId) return;
    const first = (membershipsQuery.data ?? [])[0];
    if (first) setSelectedDatasetId(first.dataset_id);
  }, [selectedTraceId, selectedDatasetId, membershipsQuery.data]);

  const handleSelectTrace = (traceId: string) => {
    setSelectedTraceId(traceId);
    setSelectedDatasetId(''); // drop the previous trace's dataset; auto-select refills
    setActiveTab('trace'); // inspect the newly selected trace first
    seedKeyRef.current = null;
  };

  const isCallQc = traceQuery.data?.name === 'call_qc';

  const memberDatasetIds = useMemo(
    () => new Set(memberships.map(m => m.dataset_id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [membershipsQuery.data],
  );
  const datasetOptions: SelectOption[] = useMemo(
    () => (datasetsQuery.data ?? []).map(d => ({ value: d.id, label: d.name })),
    [datasetsQuery.data],
  );

  const traces = tracesQuery.data ?? [];
  const busy = createItem.isPending || updateItem.isPending || deleteItem.isPending;

  // What the editor *should* be seeded from right now. While the editor still
  // holds a different target's value (target changed but the seed effect hasn't
  // run / the item is still loading), show a skeleton instead of stale content.
  const targetSeedKey =
    mode === 'update'
      ? existingItemId
        ? `upd|${existingItemId}`
        : null
      : selectedTraceId
        ? `add|${selectedTraceId}`
        : null;
  const seedPending = targetSeedKey !== null && seedKeyRef.current !== targetSeedKey;

  const canWrite = !!selectedTraceId && !!selectedDatasetId && jsonValid && !seedPending && !busy;

  const parseExpectedOutput = (): { ok: true; value: unknown } | { ok: false } => {
    const meta = inspectJson(expectedOutput);
    if (!meta.valid) {
      setJsonValid(false);
      return { ok: false };
    }
    // Empty editor → null expected output (an intentionally blank ground truth).
    return { ok: true, value: meta.parsed ?? null };
  };

  const handleAdd = () => {
    if (!selectedTraceId || !selectedDatasetId) return;
    const parsed = parseExpectedOutput();
    if (!parsed.ok) return;
    createItem.mutate(
      {
        datasetId: selectedDatasetId,
        sourceTraceId: selectedTraceId,
        expectedOutput: parsed.value,
      },
      {
        onSuccess: () => {
          toast.success('Added to Langfuse dataset.');
          onOpenChange(false);
        },
        onError: err => toast.error(err instanceof Error ? err.message : 'Failed to add.'),
      },
    );
  };

  const handleUpdate = () => {
    if (!selectedTraceId || !selectedDatasetId || !existingItemId) return;
    const parsed = parseExpectedOutput();
    if (!parsed.ok) return;
    updateItem.mutate(
      {
        datasetId: selectedDatasetId,
        itemId: existingItemId,
        sourceTraceId: selectedTraceId,
        expectedOutput: parsed.value,
      },
      {
        onSuccess: () => {
          toast.success('Updated the dataset item.');
          onOpenChange(false);
        },
        onError: err => toast.error(err instanceof Error ? err.message : 'Failed to update.'),
      },
    );
  };

  const handleRemoveConfirmed = () => {
    if (!selectedTraceId || !selectedDatasetId || !existingItemId) return;
    deleteItem.mutate(
      { datasetId: selectedDatasetId, itemId: existingItemId, sourceTraceId: selectedTraceId },
      {
        onSuccess: () => {
          toast.success('Removed from dataset.');
          setConfirmRemoveOpen(false);
          // Membership refetch flips this back to "add" mode; modal stays open.
        },
        onError: err => toast.error(err instanceof Error ? err.message : 'Failed to remove.'),
      },
    );
  };

  const removeTargetName = existingMembership?.dataset_name || 'this dataset';

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex h-[92vh] w-[min(1100px,calc(100vw-2rem))] max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4 pr-14">
            <DialogTitle>Add to Langfuse dataset</DialogTitle>
            <DialogDescription>
              Pick an evaluation trace for this call, edit the expected output, and add it to a
              dataset.
            </DialogDescription>
          </DialogHeader>

          <ErrorBoundary
            fallback={
              <div className="flex min-h-0 flex-1 items-center justify-center p-8 text-center">
                <div>
                  <p className="text-sm font-semibold">Something went wrong in this dialog.</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Close it and try again — your other work is unaffected.
                  </p>
                </div>
              </div>
            }
          >
            <div className="flex min-h-0 flex-1 overflow-hidden">
              {/* Left rail — trace list */}
              <div className="flex w-[300px] shrink-0 flex-col border-r bg-muted/30">
                <div className="border-b px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
                  Traces
                </div>
                <ScrollArea className="min-h-0 flex-1">
                  <div className="p-2">
                    {tracesQuery.isLoading ? (
                      <div className="space-y-2 p-1">
                        {[0, 1, 2, 3].map(i => (
                          <Skeleton key={i} className="h-12 w-full" />
                        ))}
                      </div>
                    ) : tracesQuery.isError ? (
                      <p className="p-2 text-sm text-destructive">
                        Failed to load traces for this call.
                      </p>
                    ) : traces.length === 0 ? (
                      <p className="p-2 text-sm text-muted-foreground">
                        No evaluation traces found for this call.
                      </p>
                    ) : (
                      traces.map(trace => (
                        <TraceListItem
                          key={trace.trace_id}
                          trace={trace}
                          active={trace.trace_id === selectedTraceId}
                          enabled={open}
                          onSelect={() => handleSelectTrace(trace.trace_id)}
                        />
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>

              {/* Right pane — Trace inspector | Add to dataset */}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {!selectedTraceId ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Select a trace to continue.
                  </div>
                ) : (
                  <Tabs
                    value={activeTab}
                    onValueChange={v => setActiveTab(v as 'trace' | 'dataset')}
                    className="flex min-h-0 flex-1 flex-col"
                  >
                    <div className="shrink-0 border-b px-4 py-2">
                      <TabsList>
                        <TabsTrigger value="trace">Trace</TabsTrigger>
                        <TabsTrigger value="dataset">Add to dataset</TabsTrigger>
                      </TabsList>
                    </div>

                    <TabsContent value="trace" className="mt-0 flex min-h-0 flex-1 flex-col p-4">
                      <TraceInspector
                        trace={traceQuery.data}
                        isLoading={traceQuery.isLoading}
                        isError={traceQuery.isError}
                      />
                    </TabsContent>

                    <TabsContent
                      value="dataset"
                      className="mt-0 flex min-h-0 flex-1 flex-col gap-4 p-4"
                    >
                      {/* Dataset picker — datasets the trace is already in are ticked */}
                      <div className="shrink-0 space-y-1.5">
                        <Label htmlFor="dataset">Dataset</Label>
                        <SearchableSelect
                          id="dataset"
                          value={selectedDatasetId}
                          onValueChange={value => setSelectedDatasetId(String(value))}
                          options={datasetOptions}
                          placeholder={
                            datasetsQuery.isLoading ? 'Loading datasets…' : 'Select a dataset…'
                          }
                          searchPlaceholder="Search datasets…"
                          emptyText="No datasets found."
                          disabled={datasetsQuery.isLoading}
                          renderOptionEnd={option =>
                            memberDatasetIds.has(String(option.value)) ? (
                              <span className="text-xs font-medium text-positive-foreground">
                                added
                              </span>
                            ) : null
                          }
                        />
                      </div>

                      {/* Expected output — fills the remaining space */}
                      <div className="flex min-h-0 flex-1 flex-col gap-1.5">
                        <Label htmlFor="expected-output">
                          Expected output
                          {mode === 'update' && (
                            <span className="ml-2 font-normal text-muted-foreground">
                              · editing existing item
                            </span>
                          )}
                        </Label>
                        {seedPending ? (
                          <Skeleton className="min-h-0 flex-1" />
                        ) : isCallQc ? (
                          <FindingsEditor
                            poolOutput={traceQuery.data?.output}
                            value={expectedOutput}
                            scorecard={scorecard}
                            className="min-h-0 flex-1"
                            onChange={(val, meta) => {
                              setExpectedOutput(val);
                              setJsonValid(meta.valid);
                            }}
                          />
                        ) : (
                          <JsonEditor
                            id="expected-output"
                            aria-label="Expected output JSON"
                            value={expectedOutput}
                            fill
                            className="min-h-0 flex-1"
                            onChange={(val, meta) => {
                              setExpectedOutput(val);
                              setJsonValid(meta.valid);
                            }}
                          />
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>
                )}
              </div>
            </div>
          </ErrorBoundary>

          <DialogFooter className="shrink-0 border-t bg-background px-6 py-4">
            {activeTab !== 'dataset' ? (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            ) : mode === 'update' ? (
              <>
                <Button
                  key="remove"
                  type="button"
                  variant="destructive"
                  className="mr-auto gap-2"
                  onClick={() => setConfirmRemoveOpen(true)}
                  disabled={busy}
                >
                  <Trash2 className="h-4 w-4" />
                  Remove from dataset
                </Button>
                <Button
                  key="cancel"
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button
                  key="submit"
                  type="button"
                  className="gap-2"
                  onClick={handleUpdate}
                  disabled={!canWrite}
                  aria-busy={updateItem.isPending}
                >
                  {updateItem.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Pencil className="h-4 w-4" />
                  )}
                  Update expected output
                </Button>
              </>
            ) : (
              <>
                <Button
                  key="cancel"
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={busy}
                >
                  Cancel
                </Button>
                <Button
                  key="submit"
                  type="button"
                  className="gap-2"
                  onClick={handleAdd}
                  disabled={!canWrite}
                  aria-busy={createItem.isPending}
                >
                  {createItem.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Database className="h-4 w-4" />
                  )}
                  Add to dataset
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <Dialog
        open={confirmRemoveOpen}
        onOpenChange={o => !deleteItem.isPending && setConfirmRemoveOpen(o)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Remove from dataset?</DialogTitle>
            <DialogDescription>
              This permanently deletes this trace&apos;s item from “{removeTargetName}”, including
              its run items. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmRemoveOpen(false)}
              disabled={deleteItem.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="gap-2"
              onClick={handleRemoveConfirmed}
              disabled={deleteItem.isPending}
              aria-busy={deleteItem.isPending}
            >
              {deleteItem.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
